import React, { useState, useEffect, useCallback } from 'react';
import api, { getErrorMessage } from '../api';
import { useToast } from '../components/Toast';
import { useDemoPrompt } from '../components/DemoWritePrompt';
import { LoadingSpinner } from '../components/UI';

const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const JOB_COLORS = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#06b6d4','#84cc16'];

function getWeekDates(offset = 0) {
  const now = new Date();
  const day = now.getDay();
  const start = new Date(now);
  start.setDate(now.getDate() - day + (offset * 7));
  start.setHours(0,0,0,0);
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    dates.push(d);
  }
  return dates;
}

function fmt(d) { return d.toISOString().split('T')[0]; }
function fmtShort(d) { return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
function fmtTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hr = parseInt(h);
  const ampm = hr >= 12 ? 'PM' : 'AM';
  return `${hr % 12 || 12}:${m} ${ampm}`;
}

export default function Schedule() {
  const toast = useToast();
  const { checkDemo, DemoModal } = useDemoPrompt();
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ employee_ids: [], job_id: '', assignment_date: '', start_time: '', end_time: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [mobile] = useState(() => window.innerWidth < 768);

  const weekDates = getWeekDates(weekOffset);
  const weekStart = fmt(weekDates[0]);
  const weekEnd = fmt(weekDates[6]);

  const loadAll = useCallback(async () => {
    try {
      const [aRes, eRes, jRes] = await Promise.all([
        api.get(`/schedule?from=${weekStart}&to=${weekEnd}`),
        api.get('/time/employees'),
        api.get('/jobs'),
      ]);
      setAssignments(aRes.data);
      setEmployees(eRes.data.filter(e => e.is_active));
      setJobs(jRes.data.filter(j => j.status === 'active'));
    } catch (err) {
      if (checkDemo(err)) return;
      toast.error(getErrorMessage(err, 'Failed to load schedule'));
    } finally { setLoading(false); }
  }, [weekStart, weekEnd]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Color map for jobs
  const jobColorMap = {};
  jobs.forEach((j, i) => { jobColorMap[j.id] = JOB_COLORS[i % JOB_COLORS.length]; });

  async function handleAdd() {
    if (!formData.employee_ids?.length || !formData.job_id || !formData.assignment_date) {
      toast.error('Select at least one employee, a job, and a date');
      return;
    }
    setSaving(true);
    try {
      for (const employee_id of formData.employee_ids) {
        await api.post('/schedule', { ...formData, employee_id });
      }
      toast.success(`${formData.employee_ids.length} assignment${formData.employee_ids.length > 1 ? 's' : ''} added`);
      setShowForm(false);
      setFormData({ employee_ids: [], job_id: '', assignment_date: '', start_time: '', end_time: '', notes: '' });
      loadAll();
    } catch (err) {
      if (checkDemo(err)) return;
      toast.error(getErrorMessage(err, 'Failed to add assignment'));
    } finally { setSaving(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Remove this assignment?')) return;
    try {
      await api.delete(`/schedule/${id}`);
      toast.success('Assignment removed');
      loadAll();
    } catch (err) {
      if (checkDemo(err)) return;
      toast.error(getErrorMessage(err, 'Failed to remove assignment'));
    }
  }

  const today = fmt(new Date());

  if (loading) return <LoadingSpinner message="Loading schedule..." />;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 13, color: 'var(--sub)' }}>
            {fmtShort(weekDates[0])} — {fmtShort(weekDates[6])}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => setWeekOffset(w => w - 1)} style={{ padding: '6px 12px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: 'var(--navy)' }}>← Prev</button>
          <button onClick={() => setWeekOffset(0)} style={{ padding: '6px 12px', background: weekOffset === 0 ? 'var(--navy)' : 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: weekOffset === 0 ? '#fff' : 'var(--navy)' }}>This Week</button>
          <button onClick={() => setWeekOffset(w => w + 1)} style={{ padding: '6px 12px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: 'var(--navy)' }}>Next →</button>
          <button onClick={() => { setShowForm(true); setFormData(f => ({ ...f, assignment_date: today })); }} style={{ padding: '8px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>+ Assign</button>
        </div>
      </div>

      {/* Add form */}
      {showForm && (
        <div className="card" style={{ padding: 20, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 14, marginBottom: 14 }}>New Assignment</div>
          <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
            <div>
             <div>
              <label className="fl">Employee(s) *</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 140, overflowY: 'auto', padding: 8, border: '1px solid var(--border)', borderRadius: 8, background: '#fff' }}>
                {employees.map(e => (
                  <label key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', padding: '4px 6px', borderRadius: 6, background: (formData.employee_ids || []).includes(e.id) ? '#eff6ff' : 'transparent' }}>
                    <input type="checkbox" checked={(formData.employee_ids || []).includes(e.id)} onChange={() => {
                      setFormData(f => {
                        const ids = f.employee_ids || [];
                        return { ...f, employee_ids: ids.includes(e.id) ? ids.filter(id => id !== e.id) : [...ids, e.id] };
                      });
                    }} />
                    <span style={{ fontWeight: 600, color: 'var(--navy)' }}>{e.name}</span>
                    <span style={{ color: 'var(--sub)', fontSize: 11 }}>{e.role || 'Employee'}</span>
                  </label>
                ))}
              </div>
            </div>
            </div>
            <div>
              <label className="fl">Job *</label>
              <select className="field" value={formData.job_id} onChange={e => setFormData(f => ({ ...f, job_id: e.target.value }))}>
                <option value="">Select job...</option>
                {jobs.map(j => <option key={j.id} value={j.id}>{j.name}{j.customer_name ? ` — ${j.customer_name}` : ''}</option>)}
              </select>
            </div>
            <div>
              <label className="fl">Date *</label>
              <input className="field" type="date" value={formData.assignment_date} onChange={e => setFormData(f => ({ ...f, assignment_date: e.target.value }))} />
            </div>
            <div>
              <label className="fl">Start Time</label>
              <input className="field" type="time" value={formData.start_time} onChange={e => setFormData(f => ({ ...f, start_time: e.target.value }))} />
            </div>
            <div>
              <label className="fl">End Time</label>
              <input className="field" type="time" value={formData.end_time} onChange={e => setFormData(f => ({ ...f, end_time: e.target.value }))} />
            </div>
            <div>
              <label className="fl">Notes</label>
              <input className="field" value={formData.notes} onChange={e => setFormData(f => ({ ...f, notes: e.target.value }))} placeholder="Optional" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={handleAdd} disabled={saving} style={{ flex: 1, padding: 11, background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Saving...' : 'Add Assignment'}</button>
            <button onClick={() => setShowForm(false)} style={{ padding: '11px 20px', background: 'var(--surface)', color: 'var(--sub)', border: '1px solid var(--border)', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Week calendar */}
      {mobile ? (
        /* Mobile: day list view */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {weekDates.map(date => {
            const dateStr = fmt(date);
            const dayAssignments = assignments.filter(a => a.assignment_date?.split('T')[0] === dateStr);
            const isToday = dateStr === today;
            return (
              <div key={dateStr} className="card" style={{ padding: 14, borderLeft: isToday ? '3px solid var(--accent)' : '3px solid transparent' }}>
                <div style={{ fontWeight: 700, color: isToday ? 'var(--accent)' : 'var(--navy)', fontSize: 13, marginBottom: dayAssignments.length ? 8 : 0 }}>
                  {DAYS[date.getDay()]} {fmtShort(date)}{isToday ? ' — Today' : ''}
                </div>
                {dayAssignments.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--muted)', fontStyle: 'italic' }}>No assignments</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {dayAssignments.map(a => (
                      <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: `${jobColorMap[a.job_id] || '#3b82f6'}12`, borderRadius: 8, borderLeft: `3px solid ${jobColorMap[a.job_id] || '#3b82f6'}` }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--navy)' }}>{a.employee_name}</div>
                          <div style={{ fontSize: 12, color: jobColorMap[a.job_id] || '#3b82f6' }}>{a.job_name}</div>
                          {(a.start_time || a.end_time) && (
                            <div style={{ fontSize: 11, color: 'var(--sub)', marginTop: 2 }}>{fmtTime(a.start_time)}{a.end_time ? ` — ${fmtTime(a.end_time)}` : ''}</div>
                          )}
                          {a.notes && <div style={{ fontSize: 11, color: 'var(--sub)', marginTop: 2 }}>{a.notes}</div>}
                        </div>
                        <button onClick={() => handleDelete(a.id)} style={{ padding: '4px 8px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#dc2626', opacity: 0.6 }}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* Desktop: week grid */
        <div className="card" style={{ overflow: 'visible' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid var(--divider)' }}>
            {weekDates.map(date => {
              const isToday = fmt(date) === today;
              return (
                <div key={fmt(date)} style={{ padding: '10px 8px', textAlign: 'center', borderRight: '1px solid var(--divider)', background: isToday ? '#eff6ff' : 'transparent' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--sub)', textTransform: 'uppercase' }}>{DAYS[date.getDay()]}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: isToday ? 'var(--accent)' : 'var(--navy)', marginTop: 2 }}>{date.getDate()}</div>
                </div>
              );
            })}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', minHeight: 200 }}>
            {weekDates.map(date => {
              const dateStr = fmt(date);
              const dayAssignments = assignments.filter(a => a.assignment_date?.split('T')[0] === dateStr);
              const isToday = dateStr === today;
              return (
                <div key={dateStr} style={{ padding: 6, borderRight: '1px solid var(--divider)', background: isToday ? '#f8fbff' : 'transparent', minHeight: 120 }}>
                  {dayAssignments.map(a => (
                    <div key={a.id} style={{ padding: '6px 8px', marginBottom: 4, borderRadius: 6, background: `${jobColorMap[a.job_id] || '#3b82f6'}15`, borderLeft: `3px solid ${jobColorMap[a.job_id] || '#3b82f6'}`, cursor: 'pointer', position: 'relative' }}
                      title={`${a.employee_name} — ${a.job_name}${a.start_time ? ` (${fmtTime(a.start_time)})` : ''}`}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)', lineHeight: 1.3 }}>{a.employee_name}</div>
                      <div style={{ fontSize: 10, color: jobColorMap[a.job_id] || '#3b82f6', fontWeight: 600 }}>{a.job_name}</div>
                      {a.start_time && <div style={{ fontSize: 10, color: 'var(--sub)' }}>{fmtTime(a.start_time)}</div>}
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(a.id); }} style={{ position: 'absolute', top: 2, right: 4, background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, color: '#dc2626', opacity: 0.5, padding: 2 }}>✕</button>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {assignments.length === 0 && !showForm && (
        <div className="card" style={{ padding: 40, textAlign: 'center', marginTop: 16 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📅</div>
          <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 6, fontSize: 15 }}>No assignments this week</div>
          <div style={{ fontSize: 13, color: 'var(--sub)', marginBottom: 18 }}>Assign employees to jobs so they know where to be and when</div>
          <button onClick={() => { setShowForm(true); setFormData(f => ({ ...f, assignment_date: today })); }} style={{ padding: '10px 22px', background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>+ Create First Assignment</button>
        </div>
      )}

      <DemoModal />
    </div>
  );
}